import React from 'react';
import EmptyState from '../../../components/shared/EmptyState';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import { formatConflictDisplay, hasConflict, formatOwnershipDisplay } from '../../../utils/songDataFormatters';

function SongTable({ songs, currentPage, totalSongs, songsPerPage, onPageChange, onSelectSong, isLoading, t }) {
  const totalPages = Math.ceil(totalSongs / songsPerPage);
  
  // Generate pages array: [1, 2, 3, 4, 5, 6, ..., 16]
  const pageNumbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    // Show 1, 2, 3, 4, 5, 6 ... totalPages
    for (let i = 1; i <= 6; i++) {
      pageNumbers.push(i);
    }
    pageNumbers.push('ellipsis');
    pageNumbers.push(totalPages);
  }

  const startIdx = totalSongs === 0 ? 0 : (currentPage - 1) * songsPerPage + 1;
  const endIdx = Math.min(currentPage * songsPerPage, totalSongs);

  return (
    <div className="song-table-container">
      {/* Table Headers */}
      <div className="song-table-grid song-table-header">
        <div>{t ? t('songs.table.songName') : 'Song Name'}</div>
        <div>{t ? t('songs.table.status') : 'Status'}</div>
        <div>{t ? t('songs.table.artist') : 'Artist'}</div>
        <div>{t ? t('songs.table.lyricsMusic') : 'Lyrics & Music'}</div>
        <div>{t ? t('songs.table.distributor') : 'Distributor'}</div>
        <div className="center-header">{t ? t('songs.table.labels') : 'Labels'}</div>
        <div className="center-header">{t ? t('songs.table.ownership') : 'Ownership'}</div>
        <div>{t ? t('songs.table.notesCases') : 'Notes / Cases'}</div>
        <div>{t ? t('songs.table.conflicts') : 'Conflicts'}</div>
      </div>

      {/* Table Body Rows */}
      <div className="song-table-body">
        {isLoading ? (
          <LoadingOverlay isLoading message={t ? t('songs.table.loadingMessage') : 'Fetching songs, please wait.'} />
        ) : songs.length === 0 ? (
          <EmptyState
            hasSearch={true}
            title={t ? t('songs.table.emptyTitle') : 'No songs found'}
            subtitle={t ? t('songs.table.emptySubtitle') : 'No songs found matching your search, sort, or filter criteria.'}
          />
        ) : (
          songs.map((song, index) => {
            // First row (index 0) gets the blue outline highlight matching the screenshot
            const isFirstRow = index === 0;
            const conflictValue = {
              conflicts: song.conflicts ?? song.songConflicts ?? song.song_conflicts ?? song.conflictRecords ?? song.conflict_records,
              conflictCount: song.conflictCount ?? song.conflictsCount,
              activeConflictCount: song.activeConflictCount,
              conflict: song.conflict,
            };
            return (
              <div
                key={song.id || index}
                className={`song-table-grid song-row ${isFirstRow ? 'highlighted-row' : ''}`}
                onClick={() => onSelectSong && onSelectSong(song)}
              >
                {/* Song Name */}
                <div className="cell-song-name">
                  <div className="main-text">{song.nameSinhala || song.name}</div>
                  <div className="sub-text">{song.name}</div>
                </div>

                {/* Status */}
                <div>
                  {(() => {
                    const isAct = song.status === 'Active' || song.status === 1 || song.status === true || song.status === '1';
                    return (
                      <span className={`status-pill ${isAct ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {isAct ? (t ? t('common.statusActive') : 'Active') : (t ? t('common.statusInactive') : 'Inactive')}
                      </span>
                    );
                  })()}
                </div>

                {/* Artist */}
                <div>
                  <div className="main-text">{(song.artist || '').split(',').map(name => name.trim()).filter(Boolean).map(name => name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')).join(' / ')}</div>
                </div>

                {/* Lyrics & Music */}
                <div>
                  <div className="main-text lyrics-text">{song.lyrics}</div>
                  <div className="sub-text music-text">{song.music}</div>
                </div>

                {/* Distributor */}
                <div>
                  <div className="main-text" style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>
                    {song.distributionProvider || song.distributor || song.distributorName || '—'}
                  </div>
                </div>

                {/* Record Labels (Overlapping Avatars Stack with Actual Images) */}
                <div className="cell-labels center-text">
                  <LabelAvatarStack labels={song.labels || song.recordLabels} />
                </div>

                {/* Ownership */}
                <div className="cell-ownership center-text">
                  {formatOwnershipDisplay(song.ownership !== undefined ? song.ownership : (song.ownershipPercentage !== undefined ? song.ownershipPercentage : 0))}
                </div>

                {/* Notes / Cases */}
                <div className="cell-notes">
                  {song.notes}
                </div>

                {/* Conflicts */}
                <div className="cell-conflicts">
                  <span className={`conflicts-badge ${hasConflict(conflictValue) ? 'conflict' : 'no-conflict'}`}>
                    {formatConflictDisplay(conflictValue)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Footer */}
      <div className="table-pagination-footer">
        <div className="pagination-info">
          {startIdx}-{endIdx} of {totalSongs}
        </div>
        
        <div className="pagination-controls">
          {/* Double Left Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange(1)}
            aria-label="First Page"
          >
            &lt;&lt;
          </button>
          
          {/* Single Left Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous Page"
          >
            &lt;
          </button>

          {/* Page Numbers */}
          {pageNumbers.map((num, i) => {
            if (num === 'ellipsis') {
              return <span key={`ellipsis-${i}`} className="pagination-ellipsis">...</span>;
            }
            return (
              <button
                key={`page-${num}`}
                className={`pagination-btn num-btn ${currentPage === num ? 'active' : ''}`}
                onClick={() => onPageChange(num)}
              >
                {num}
              </button>
            );
          })}

          {/* Single Right Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next Page"
          >
            &gt;
          </button>

          {/* Double Right Arrow */}
          <button
            className="pagination-btn arrow-btn"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
            aria-label="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    </div>
  );
}

export default SongTable;
